import { Component } from '@theme/component';
import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const IMAGE_WIDTHS = [300, 450, 600, 800, 1000];

// Tokenless Storefront API: prices a throwaway cart with live automatic discounts, leaving the shopper's cart alone.
const PRICE_URL = '/api/2025-07/graphql.json';
const PRICE_QUERY = `mutation BundlePrice($lines: [CartLineInput!], $country: CountryCode) @inContext(country: $country) {
  cartCreate(input: { lines: $lines }) {
    cart {
      cost { subtotalAmount { amount } }
      discountAllocations { discountedAmount { amount } }
    }
  }
}`;

/**
 * @typedef {{ id: number, available: boolean, price: number, title: string, options: string[], featured_image: { src: string } | null }} Variant
 */

/**
 * @typedef {object} BundleRefs
 * @property {HTMLElement[]} items - One per bundle product; each holds its variants JSON.
 * @property {HTMLSelectElement[]} selects - Variant selects submitted to the cart (no-JS fallback UI).
 * @property {HTMLElement[]} prices - Per-piece price, or sold out.
 * @property {HTMLElement} [compare] - Pre-discount total.
 * @property {HTMLElement} total - Bundle total.
 * @property {HTMLElement} [savings] - Savings message.
 * @property {HTMLElement} [badge] - Header savings badge.
 * @property {HTMLButtonElement} submit - Add bundle button.
 * @property {HTMLElement} status - Live region for add-to-cart feedback.
 */

/**
 * Picks one variant per bundle product and adds them all to cart in a single request.
 *
 * @extends {Component<BundleRefs>}
 */
class BundleComponent extends Component {
  requiredRefs = ['total', 'submit', 'status'];

  /** @type {Variant[][]} */
  #variants = [];

  /** @type {AbortController | null} */
  #priceRequest = null;

  /** Last live savings in minor units; stands in for the settings estimate while the next price loads. */
  /** @type {number | null} */
  #liveSavings = null;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#priceRequest?.abort();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  #init() {
    this.#variants = this.refs.items.map((item) =>
      JSON.parse(item.querySelector('script[type="application/json"]')?.textContent || '[]')
    );
    this.#update();
  }

  handleOptionChange = () => this.#update();

  #update() {
    const { items, selects, prices, submit } = this.refs;
    const { moneyFormat = '{{amount}}', currency = 'USD' } = this.dataset;
    const discount = Number(this.dataset.discount) || 0;
    const discountCents = Number(this.dataset.discountCents) || 0;
    let sum = 0;
    let allAvailable = true;

    items.forEach((item, index) => {
      const variants = this.#variants[index] ?? [];
      const fieldsets = [...item.querySelectorAll('fieldset')];
      const selected = fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value);
      const variant = variants.find((v) => v.options.every((value, i) => value === selected[i]));

      // Strike through values that are sold out given the other selected options.
      fieldsets.forEach((fieldset, optionIndex) => {
        for (const input of fieldset.querySelectorAll('input')) {
          const available = variants.some(
            (v) =>
              v.available &&
              v.options[optionIndex] === input.value &&
              v.options.every((value, i) => i === optionIndex || value === selected[i])
          );
          input.closest('label')?.toggleAttribute('data-unavailable', !available);
        }
        const label = fieldset.querySelector('[data-selected-value]');
        if (label) label.textContent = selected[optionIndex] ?? '';
      });

      if (!variant?.available) allAvailable = false;
      if (!variant) return;

      sum += variant.price;
      const select = selects[index];
      if (select) select.value = String(variant.id);
      const price = prices[index];
      if (price) {
        price.textContent = variant.available
          ? formatMoney(variant.price, moneyFormat, currency)
          : this.dataset.soldOutMessage ?? '';
      }

      // ponytail: assumes the first option (color) drives the photo; falls back to any variant sharing it.
      const image =
        variant.featured_image ??
        variants.find((v) => v.options[0] === variant.options[0] && v.featured_image)?.featured_image;
      const img = item.querySelector('img');
      if (image && img) this.#swapImage(img, image.src);
    });

    // Estimate first (last live savings, else settings), then the live price replaces it.
    const estimate =
      this.#liveSavings === null
        ? Math.round((sum * (100 - discount)) / 100) - discountCents
        : sum - this.#liveSavings;
    this.#renderTotals(sum, Math.max(0, estimate));
    submit.disabled = !allAvailable;
    if (allAvailable) this.#fetchLiveTotal(sum);
  }

  /**
   * @param {number} sum - Pre-discount total in minor units.
   * @param {number} bundleTotal - Discounted total in minor units.
   */
  #renderTotals(sum, bundleTotal) {
    const { total, compare, savings, badge } = this.refs;
    const { moneyFormat = '{{amount}}', currency = 'USD' } = this.dataset;
    const saved = sum - bundleTotal;
    const savedMoney = formatMoney(saved, moneyFormat, currency);

    total.textContent = formatMoney(bundleTotal, moneyFormat, currency);
    if (compare) {
      compare.textContent = formatMoney(sum, moneyFormat, currency);
      compare.hidden = saved <= 0;
    }
    if (savings && this.dataset.savingsTemplate) {
      savings.textContent = this.dataset.savingsTemplate.replace('[amount]', savedMoney);
      savings.hidden = saved <= 0;
    }
    if (badge && this.dataset.badgeTemplate) {
      badge.textContent = this.dataset.badgeTemplate.replace('[amount]', savedMoney);
      badge.hidden = saved <= 0;
    }
  }

  /** @param {number} sum - Pre-discount total in minor units. */
  async #fetchLiveTotal(sum) {
    this.#priceRequest?.abort();
    const controller = new AbortController();
    this.#priceRequest = controller;
    const { currency = 'USD', country } = this.dataset;
    const lines = this.refs.selects.map((select) => ({
      merchandiseId: `gid://shopify/ProductVariant/${select.value}`,
      quantity: 1,
    }));

    try {
      const response = await fetch(PRICE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query: PRICE_QUERY, variables: { lines, country } }),
        signal: controller.signal,
      });
      const cart = (await response.json())?.data?.cartCreate?.cart;
      if (!cart || controller.signal.aborted) return;

      // ponytail: parser reads Storefront decimals ("120.0") correctly for 2-decimal currencies only; fine for USD.
      const toMinor = (/** @type {string} */ amount) => convertMoneyToMinorUnits(amount, currency) ?? 0;
      const cartDiscounts = cart.discountAllocations.reduce(
        (/** @type {number} */ acc, /** @type {any} */ allocation) => acc + toMinor(allocation.discountedAmount.amount),
        0
      );
      const bundleTotal = Math.max(0, toMinor(cart.cost.subtotalAmount.amount) - cartDiscounts);
      this.#liveSavings = sum - bundleTotal;
      this.#renderTotals(sum, bundleTotal);
    } catch {
      // Keep the settings-based estimate when the request fails or is superseded.
    }
  }

  /**
   * @param {HTMLImageElement} img
   * @param {string} src
   */
  #swapImage(img, src) {
    const url = new URL(src, window.location.href);
    url.searchParams.set('width', '1000');
    if (img.src === url.href) return;

    img.srcset = IMAGE_WIDTHS.map((width) => {
      url.searchParams.set('width', String(width));
      return `${url.href} ${width}w`;
    }).join(', ');
    img.src = url.href;
  }

  /** @param {SubmitEvent} event */
  handleSubmit = async (event) => {
    event.preventDefault();
    const { submit, status, selects } = this.refs;
    if (submit.disabled) return;

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    status.textContent = '';

    const items = selects.map((select) => ({ id: Number(select.value), quantity: 1 }));
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      }).then((res) => res.json());

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      const didError = Boolean(response.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError,
          items: cart.items,
          source: 'bundle-component',
          sourceId: this.id,
          itemCount: items.length,
          sections: didError ? undefined : response.sections,
        },
      });

      if (didError) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        status.textContent = response.description || response.message || this.dataset.errorMessage || '';
        return;
      }

      status.textContent = this.dataset.addedMessage ?? '';
      // Always reveal the cart (even with auto-open off) so the next step is checkout; cart page mode has no drawer.
      const drawer = /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
        document.querySelector('cart-drawer-component')?.closest('theme-drawer') ?? null
      );
      if (!drawer) window.location.assign(Theme.routes.cart_url);
      else if (!drawer.hasAttribute('open')) drawer.open();
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      status.textContent = this.dataset.errorMessage ?? '';
    } finally {
      submit.removeAttribute('aria-busy');
      this.#update();
    }
  };
}

if (!customElements.get('bundle-component')) {
  customElements.define('bundle-component', BundleComponent);
}
